import {
  type FinalWordData,
  WordCloud as ReactWordCloud,
} from "@isoterik/react-word-cloud";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { cn } from "../lib/cn";
import { scaleWordCloudValues } from "./scale-word-cloud-values";
import type { ChartWordCloudBasis, ChartWordCloudItem } from "./types";

type ChartWordCloudClickEvent = ReactMouseEvent<SVGTextElement, MouseEvent>;

type ChartWordCloudProps = {
  basis?: ChartWordCloudBasis;
  className?: string;
  emptyMessage?: string;
  height?: number;
  isLoading?: boolean;
  onWordClick?: (
    word: FinalWordData,
    index: number,
    event: ChartWordCloudClickEvent,
  ) => void;
  wordCloudId?: string;
  words: ChartWordCloudItem[];
};

function ChartWordCloud({
  basis,
  className,
  emptyMessage = "No data yet.",
  height = 440,
  isLoading = false,
  onWordClick,
  wordCloudId,
  words,
}: ChartWordCloudProps) {
  const fallbackWordCloudId = useId();
  const resolvedWordCloudId = wordCloudId ?? fallbackWordCloudId;
  const { setRef, wordCloudSize } = useResponsiveWordCloud();
  const shortestSide = Math.min(wordCloudSize.width, wordCloudSize.height);
  const minScale = clamp(shortestSide * 0.035, 14, 24);
  const maxScale = clamp(shortestSide * 0.13, 42, 92);
  const scaledWords = scaleWordCloudValues(words, minScale, maxScale, basis);
  const hasMeasuredSize = wordCloudSize.width > 0 && wordCloudSize.height > 0;

  const getColor = useCallback(
    (value: number) => {
      const fontSize = Math.sqrt(Math.max(value, 0));
      const normalized =
        (fontSize - minScale) / Math.max(maxScale - minScale, 1);

      if (normalized > 0.8) return "#1f2937";
      if (normalized > 0.5) return "#4b5563";
      if (normalized > 0.3) return "#6b7280";

      return "#9ca3af";
    },
    [maxScale, minScale],
  );

  return (
    <div
      aria-busy={isLoading || (scaledWords.length > 0 && !hasMeasuredSize)}
      data-testid="chart-word-cloud"
      data-wordcloud-id={resolvedWordCloudId}
      ref={setRef}
      className={cn(
        "syncly-chart-word-cloud flex h-full w-full overflow-hidden",
        className,
      )}
      style={{ minHeight: height }}
    >
      <div className="flex h-full min-h-[inherit] w-full flex-1 items-center justify-center">
        {isLoading ? null : scaledWords.length === 0 ? (
          <div className="text-center" role="status">
            <p className="text-sm font-medium text-slate-700">{emptyMessage}</p>
          </div>
        ) : !hasMeasuredSize ? null : (
          <ReactWordCloud
            fill={(word: FinalWordData) => getColor(word.value)}
            font="pretendard"
            fontWeight={600}
            height={wordCloudSize.height}
            onWordClick={(
              word: FinalWordData,
              index: number,
              event: ChartWordCloudClickEvent,
            ) => {
              onWordClick?.(word, index, event);
            }}
            padding={5}
            rotate={() => 0}
            spiral="archimedean"
            width={wordCloudSize.width}
            words={scaledWords}
          />
        )}
      </div>
      <style>{`
        .syncly-chart-word-cloud text {
          paint-order: stroke fill;
          cursor: pointer;
          transition: all 0.2s;
          stroke-width: 0.15em;
          stroke: transparent;
        }
        .syncly-chart-word-cloud text:hover {
          fill: hsl(var(--primary)) !important;
          stroke: var(--color-rebranding-600);
        }
      `}</style>
    </div>
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function useResponsiveWordCloud() {
  const [wordCloudSize, setWordCloudSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateSize = useCallback(() => {
    if (!containerRef.current) return;

    const { width, height } = containerRef.current.getBoundingClientRect();

    setWordCloudSize((currentSize) => {
      if (currentSize.width === width && currentSize.height === height) {
        return currentSize;
      }

      return { width, height };
    });
  }, []);

  const setRef = useCallback(
    (element: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();

      if (!element) {
        containerRef.current = null;
        return;
      }

      containerRef.current = element;
      updateSize();

      const observer = new ResizeObserver(() => {
        updateSize();
      });

      resizeObserverRef.current = observer;
      observer.observe(element);
    },
    [updateSize],
  );

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  return {
    setRef,
    wordCloudSize,
  };
}

export { ChartWordCloud };
export type { ChartWordCloudClickEvent, ChartWordCloudProps };
